import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/config';
import { applyOrgFilter, getActiveOrgId } from '@/lib/activeOrg';
import { mapFxBankConnection, mapFxBankAccount } from '@/lib/mappers';
import { supabase } from '@/lib/supabase';
import { FxBankConnection, FxBankAccount } from '@/types/api/finance';

async function invokeEdgeFunction(
  functionName: string,
  body: Record<string, unknown>
): Promise<any> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id ?? '';

  const res = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token ?? ''}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ ...body, userId }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Edge function error ${res.status}`);
  }

  return res.json();
}

const financePlaidService = {
  async createLinkToken(): Promise<{ linkToken: string }> {
    return invokeEdgeFunction('fx-plaid-link-token', {});
  },

  async exchangeToken(publicToken: string): Promise<{
    success: boolean;
    itemId: string;
    accounts: FxBankAccount[];
  }> {
    return invokeEdgeFunction('fx-plaid-exchange-token', {
      publicToken,
      organizationId: getActiveOrgId(),
    });
  },

  async getConnections(): Promise<FxBankConnection[]> {
    let query = supabase
      .from('fx_bank_connections')
      .select('*, fx_bank_accounts(*)')
      .order('created_at', { ascending: false });

    query = applyOrgFilter(query);

    const { data, error } = await query;

    if (error) throw error;

    return (data ?? []).map((row: any) => ({
      ...mapFxBankConnection(row),
      accounts: (row.fx_bank_accounts ?? []).map(mapFxBankAccount),
    }));
  },

  async syncConnection(
    connectionId: string,
    options?: { startDate?: string; endDate?: string }
  ): Promise<{
    recordsCreated: number;
    recordsUpdated: number;
    categorized?: { byRules: number; byAi: number };
  }> {
    return invokeEdgeFunction('fx-plaid-sync', {
      connectionId,
      startDate: options?.startDate,
      endDate: options?.endDate,
    });
  },

  async deleteConnection(connectionId: string): Promise<void> {
    const { error } = await supabase.from('fx_bank_connections').delete().eq('id', connectionId);

    if (error) throw error;
  },
};

export default financePlaidService;
